import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWithAuth, COMPANY_SETTINGS_API } from '../utils/api';
import ddLogo from '../components/ui/dd.png';

// Type definitions
interface CompanyLogoHookReturn {
  logoUrl: string;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CompanySettingsResponse {
  logoUrl?: string;
  [key: string]: any;
}

/**
 * Custom hook to fetch and provide company logo with fallback to Dance Digital logo
 */
export const useCompanyLogo = (): CompanyLogoHookReturn => {
  const [logoUrl, setLogoUrl] = useState<string>(ddLogo); // Default to DD logo
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasInitialized = useRef<boolean>(false);

  // Only log once per component instance
  if (!hasInitialized.current) {
    console.log('useCompanyLogo: Hook initialized, default logo:', ddLogo);
    hasInitialized.current = true;
  }

  const fetchCompanyLogo = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('useCompanyLogo: Fetching company settings...');
      console.log('useCompanyLogo: API URL:', COMPANY_SETTINGS_API);
      
      // Fetch company settings to get the logo URL
      const data: CompanySettingsResponse = await fetchWithAuth(COMPANY_SETTINGS_API);
      
      // API response logging removed to reduce console spam
      
      if (data && data.logoUrl && data.logoUrl.trim()) {
        // For PDF generation, we'll trust that the URL exists and let the PDF generator handle loading
        // This avoids CORS issues in the browser context while still allowing PDF generation to work
        // Custom logo URL found - logging removed to reduce console spam
        setLogoUrl(data.logoUrl);
        setIsLoading(false);
        
        // Optional: Test if the logo URL is accessible for UI display
        // But don't fail if it doesn't load in browser context
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const timeoutId = setTimeout(() => {
          // Logo loading test timed out - logging removed to reduce console spam
        }, 3000); // Shorter timeout since this is just a test
        
        img.onload = () => {
          clearTimeout(timeoutId);
          // Custom logo loads in browser context - logging removed to reduce console spam
        };
        
        img.onerror = () => {
          clearTimeout(timeoutId);
          console.log('useCompanyLogo: Logo fails in browser context (likely CORS), but keeping URL for PDF generation');
        };
        
        // Test load the image, but don't fail if it doesn't work
        img.src = data.logoUrl;
      } else {
        // No company logo set, use DD logo
        console.log('useCompanyLogo: No custom logo URL found in API response, using DD logo');
        setLogoUrl(ddLogo);
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('useCompanyLogo: Error fetching company logo:', err);
      console.error('useCompanyLogo: Error details:', {
        message: err.message,
        status: err.status,
        response: err.response
      });
      
      // Only set error for network/auth issues, not for missing logos
      if (err.message?.includes('fetch') || err.message?.includes('auth')) {
        setError(err.message);
      }
      setLogoUrl(ddLogo); // Fallback to DD logo on error
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('useCompanyLogo: useEffect triggered, calling fetchCompanyLogo');
    fetchCompanyLogo();
  }, [fetchCompanyLogo]);

  return { logoUrl, isLoading, error, refetch: fetchCompanyLogo };
};

export default useCompanyLogo;



